<template>
  <div class="vehicle-todo-list">
    <div class="todo-work-area">
      <div class="todo-table-container">
        <div class="todo-toolbar">
          <div v-if="!isFleet && !isWorkshopLink && isDraftMode" class="todo-context-line">
            Vehicle will be assigned when the vehicle is saved.
          </div>
          <div v-if="isFleet" class="todo-filter-summary">
            <div class="todo-filter-action">
              <ButtonFilter
                :buttonText="filterButtonText"
                filterPosition="left"
                :filterWidth="420"
                :isOpen.sync="filterOpen"
              >
                <div class="todo-filter-panel" @click.stop>
                  <MultiSelect
                    :items="vehicleSelectorItems"
                    :value="draftSelectedVehicles"
                    itemKey="label"
                    saveKey="Inventory #"
                    itemDesc="Inventory Code"
                    :tableBoxHideColumns="['label', '_vehicleCategoryCode']"
                    placeholder="Vehicles"
                    @update:value="draftSelectedVehicles = $event"
                  />
                  <Dropdown
                    :items="teamFilterItems"
                    itemKey="label"
                    :selectedObject.sync="draftTeamFilter"
                    placeholder="Vehicle Team"
                  />
                  <Dropdown
                    :items="categoryFilterItems"
                    itemKey="label"
                    :selectedObject.sync="draftCategoryFilter"
                    placeholder="Vehicle Category"
                  />
                  <Dropdown
                    :items="statusFilterItems"
                    itemKey="label"
                    :selectedObject.sync="draftStatusFilter"
                    placeholder="Status"
                  />
                  <Dropdown
                    :items="dateFieldItems"
                    itemKey="label"
                    :selectedObject.sync="draftDateField"
                    placeholder="Date Field"
                  />
                  <div class="todo-filter-date-row">
                    <DateTimePicker :value.sync="draftDateFrom" :showTime="false" placeholder="From" />
                    <DateTimePicker :value.sync="draftDateTo" :showTime="false" placeholder="To" />
                  </div>
                  <div class="todo-filter-actions">
                    <button type="button" @click="clearFilters">Clear</button>
                    <div class="todo-filter-actions-right">
                      <button type="button" @click="applyFilterChanges">Refresh</button>
                      <button type="button" @click="filterOpen = false">Cancel</button>
                    </div>
                  </div>
                </div>
              </ButtonFilter>
            </div>
          </div>
          <div v-if="showStatusCounts" class="todo-counts todo-link-status-counts">
            <button class="todo-status-count todo-count-pending" :class="{ active: statusVisibility.Pending }" @click="toggleStatusVisibility('Pending')">
              <span class="todo-count-dot todo-count-dot-pending"></span>Pending: {{ pendingCount }}
            </button>
            <button class="todo-status-count todo-count-completed" :class="{ active: statusVisibility.Completed }" @click="toggleStatusVisibility('Completed')">
              <span class="todo-count-dot todo-count-dot-completed"></span>Completed: {{ completedCount }}
            </button>
            <button class="todo-status-count todo-count-cancelled" :class="{ active: statusVisibility.Cancelled }" @click="toggleStatusVisibility('Cancelled')">
              <span class="todo-count-dot todo-count-dot-cancelled"></span>Cancelled: {{ cancelledCount }}
            </button>
          </div>
          <div class="todo-actions">
            <Button v-if="(!isWorkshopLink || deferLinking) && canEdit" :disabled="editorShow" @click="openAdd">Add Task</Button>
            <Button v-if="!isWorkshopLink && canEdit" :disabled="editorShow || completionActionLoading || selectedRows.length != 1 || !selectedRowIsActive" @click="cancelSelected">Delete Task</Button>
            <Button
              v-if="(!isWorkshopLink || deferLinking) && canEdit"
              :disabled="editorShow || completionActionLoading || (!allSelectedRowsOutstanding && !allSelectedRowsCompleted)"
              @click="completionActionClicked"
              >{{ completionActionLabel }}</Button
            >
            <Button v-if="isWorkshopLink && canEdit && !deferLinking && selectedRows.length > 0" :disabled="!jobNo" @click="linkSelected">Link</Button>
            <Button v-if="isWorkshopLink && canEdit && !deferLinking && selectedRows.length > 0" :disabled="!jobNo" @click="unlinkSelected">Unlink</Button>
          </div>
        </div>

        <TableBox
          :key="`${groupByVehicle ? 'grouped-by-vehicle' : 'all-rows'}-${showCancelledTasksValue ? 'with-cancelled' : 'without-cancelled'}-${showAttachedTasksValue ? 'with-attached' : 'without-attached'}-${statusVisibilityKey}`"
          :tableData="displayRows"
          :selectedRow.sync="selectedRows"
          title="Tasks"
          :loading="loading"
          :maxSelection="viewOnly ? 0 : (isWorkshopLink || isFleet ? -1 : 1)"
          :showSelectAll="false"
          :showClearSelected="isFleet || (!isWorkshopLink && !viewOnly)"
          :dataType="tableDataType"
          :hideColumns="hiddenColumns"
          :fitContentWidth="fitContentWidth"
          :groupBy="groupByColumn"
          :columnOrderViews.sync="columnOrderViews"
          :columnOrderID="columnOrderID"
          :showRefreshButton="!isWorkshopLink"
          :additionalButtons="todoTableAdditionalButtons"
          @refreshClicked="refreshRows"
          @buttonClicked="todoTableButtonClicked"
        />
        <div v-if="isFleet" class="todo-table-options">
          <CheckBoxV2 title="Group by Vehicle" :isChecked.sync="groupByVehicle" />
        </div>
      </div>

      <div v-if="editorShow" class="todo-editor-container">
        <div class="todo-editor-header">
          <div class="todo-form-title">{{ editorAction == 'Add' ? 'Add Task' : 'Edit Task' }}</div>
          <div class="cta-group">
          <Button v-if="!editorViewOnly" :disabled="editorSaveLoading || editorTaskIsCancelled" @click="saveEditor">Save</Button>
          <Button :disabled="editorSaveLoading" @click="cancelEditor">{{ editorViewOnly ? 'Close' : 'Cancel' }}</Button>
          </div>
        </div>
        <div class="todo-editor">
          <MultiSelect
            v-if="isFleet && editorAction == 'Add'"
            :items="vehicleSelectorItems"
            :value="editorVehicles"
            itemKey="label"
            saveKey="Inventory #"
            itemDesc="Inventory Code"
            :tableBoxHideColumns="['label', '_vehicleCategoryCode']"
            placeholder="Search Vehicles"
            @update:value="editorVehiclesSelected"
          />
          <Input
            placeholder="Task Name"
            :inputValue.sync="editorTaskName"
            :isRequired="true"
            :errorMessage.sync="editorTaskNameError"
            :isDisabled="editorViewOnly"
            validator="text,100"
          />
          <DateTimePicker
            placeholder="ETA/Due Date"
            :value.sync="editorDueDate"
            :showTime="false"
            :isDisabled="editorViewOnly"
          />
          <CheckBoxV2
            title="Completed"
            :isChecked.sync="editorCompleted"
            :isDisabled="editorViewOnly"
            @checkBoxClicked="editorCompletionChanged"
          />
          <DateTimePicker
            placeholder="Completion Date"
            :value.sync="editorCompletionDate"
            :showTime="false"
            :isDisabled="editorViewOnly || !editorCompleted"
          />
          <RemarksBox
            class="todo-editor-remarks"
            :text.sync="editorRemarks"
            title="Remarks"
            :isDisabled="editorViewOnly"
            :maxLength="200"
          />
          <InputQuery
            v-if="editorVehicles.length == 1 && !isDraftMode && !isWorkshopLink"
            placeholder="Workshop Job"
            :selectedObject.sync="editorJob"
            :items="editorJobItems"
            itemKey="label"
            :tableBoxHideColumns="['label']"
            :isDisabled="editorViewOnly"
          />
          <div v-else-if="editorVehicles.length > 1" class="todo-help-text">
            Workshop Job linking can be added separately for each vehicle after bulk creation.
          </div>
          <div v-if="editorTaskIsCancelled && !editorViewOnly" class="todo-editor-reactivate">
            <Button :disabled="editorSaveLoading" @click="reactivateEditorTask">Reinstate Task</Button>
          </div>
        </div>
      </div>
    </div>

    <PopupV2
      :show="cancelPopupShow"
      title="Delete Task"
      width="420px"
      :buttons="cancelButtons"
      @buttonClicked="cancelButtonClicked"
    >
      <div class="todo-editor">
        <div class="todo-help-text">Enter the cancellation reason. It will be appended to the existing Remarks.</div>
        <div v-if="cancelExistingRemarks" class="todo-existing-remarks">{{ cancelExistingRemarks }}</div>
        <RemarksBox :text.sync="cancelRemarks" title="Cancellation Reason" :maxLength="200" />
      </div>
    </PopupV2>

    <PopupV2
      :show="completionPopupShow"
      title="Mark Completed"
      width="420px"
      :buttons="completionButtons"
      @buttonClicked="completionButtonClicked"
    >
      <div class="todo-editor">
        <div class="todo-help-text">Set the completion date for {{ selectedRows.length }} selected task{{ selectedRows.length == 1 ? '' : 's' }}.</div>
        <DateTimePicker
          placeholder="Completion Date"
          :value.sync="completionDate"
          :showTime="false"
          :isRequired="true"
          :errorMessage.sync="completionDateError"
        />
      </div>
    </PopupV2>

    <PopupV2
      :show="reviewPopupShow"
      title="Bulk Task Add Review"
      width="720px"
      :buttons="[{ text: 'Close' }]"
      @buttonClicked="reviewPopupShow = false"
    >
      <TableBox
        :tableData="reviewRows"
        title="Bulk Add Result"
        :maxSelection="0"
        :dataType="reviewDataType"
        :hideColumns="[]"
      />
    </PopupV2>
  </div>
</template>

<script>
/* eslint-disable */
import MultiSelect from '@/components/v2testComponent/MultiSelect.vue';

const formatDate = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export default {
  components: {
    MultiSelect,
  },
  props: {
    mode: { type: String, default: 'fleet' }, // fleet | vehicle | workshop-link
    vehicleNo: { type: String, default: '' },
    jobNo: { type: String, default: '' },
    viewOnly: { type: Boolean, default: false },
    fitContentWidth: { type: Boolean, default: false },
    deferLinking: { type: Boolean, default: false },
    deferredSelectedTodoIDs: { type: Array, default: null },
    deferredTodoUpdates: { type: Array, default: null },
    showAttachedTasks: { type: Boolean, default: false },
    showCancelledTasks: { type: Boolean, default: false },
    draft: { type: Boolean, default: false },
    draftItems: { type: Array, default: function () { return []; } },
  },
  data: function () {
    return {
      rows: [],
      selectedRows: [],
      loading: false,
      filterOpen: false,
      vehicleSelectorItems: [],
      vehicleCategoryItems: [],
      selectedVehicles: [],
      draftSelectedVehicles: [],

      teamFilter: null,
      categoryFilter: null,
      statusFilter: { label: 'All', value: 'All' },
      dateField: { label: 'Due Date', value: 'DueDate' },
      dateFrom: null,
      dateTo: null,
      localShowCancelledTasks: false,
      groupByVehicle: false,
      statusVisibility: { Pending: true, Completed: true, Cancelled: false },
      draftTeamFilter: null,
      draftCategoryFilter: null,
      draftStatusFilter: { label: 'All', value: 'All' },
      draftDateField: { label: 'Due Date', value: 'DueDate' },
      draftDateFrom: null,
      draftDateTo: null,

      editorShow: false,
      editorAction: '',
      editorTaskName: '',
      editorTaskNameError: '',
      editorDueDate: null,
      editorCompleted: false,
      editorCompletionDate: null,
      editorRemarks: '',
      editorJob: null,
      editorJobItems: [],
      editorVehicles: [],
      editorRow: null,
      editorViewOnly: false,

      cancelPopupShow: false,
      cancelRemarks: '',
      cancelExistingRemarks: '',
      completionPopupShow: false,
      completionActionLoading: false,
      completionDate: null,
      completionDateError: '',
      editorSaveLoading: false,
      reviewPopupShow: false,
      reviewRows: [],
      columnOrderViews: [],
      columnOrderID: null,
      deferredOriginalTodoIDs: [],
      deferredTodoUpdateRows: [],
      deferredTodoCreateRows: [],
      deferredTodoReactivationRows: [],
    };
  },
  computed: {
    isFleet: function () { return this.mode == 'fleet'; },
    isWorkshopLink: function () { return this.mode == 'workshop-link'; },
    isDraftMode: function () { return this.draft || this.mode == 'draft'; },
    canEdit: function () { return !this.viewOnly; },
    selectedRow: function () { return this.selectedRows.length > 0 ? this.selectedRows[0] : null; },
    selectedRowIsActive: function () { return !!this.selectedRow && this.selectedRow['_recordState'] != 'C' && this.todoStatus(this.selectedRow) != 'Cancelled'; },
    allSelectedRowsOutstanding: function () {
      return this.selectedRows.length > 0 && this.selectedRows.every((row) => this.todoStatus(row) == 'Pending' && row['_recordState'] != 'C');
    },
    allSelectedRowsCompleted: function () {
      return this.selectedRows.length > 0 && this.selectedRows.every((row) => this.todoStatus(row) == 'Completed' && row['_recordState'] != 'C');
    },
    editorTaskIsCancelled: function () {
      return !!this.editorRow && (this.editorRow['_recordState'] == 'C' || this.todoStatus(this.editorRow) == 'Cancelled');
    },
    completionActionLabel: function () {
      return this.allSelectedRowsCompleted ? 'Mark as Uncompleted' : 'Mark Completed';
    },
    statusCountRows: function () {
      if (!this.isWorkshopLink) return this.rows;
      const currentJobNo = String(this.jobNo || '').trim();
      return this.rows.filter((row) => {
        const linkedJobNo = this.todoJobNo(row);
        const attachedToCurrentJob = currentJobNo && linkedJobNo == currentJobNo;
        return !linkedJobNo || attachedToCurrentJob || this.showAttachedTasksValue;
      });
    },
    pendingCount: function () { return this.statusCountRows.filter((row) => this.todoStatus(row) == 'Pending').length; },
    completedCount: function () { return this.statusCountRows.filter((row) => this.todoStatus(row) == 'Completed').length; },
    cancelledCount: function () { return this.statusCountRows.filter((row) => this.todoStatus(row) == 'Cancelled').length; },
    showAttachedTasksValue: function () { return this.isWorkshopLink ? this.showAttachedTasks : false; },
    showCancelledTasksValue: function () { return this.isWorkshopLink ? this.showCancelledTasks : this.localShowCancelledTasks; },
    showStatusCounts: function () { return this.isFleet || this.isWorkshopLink && this.deferLinking || !this.isFleet && !this.isWorkshopLink; },
    statusVisibilityKey: function () { return Object.keys(this.statusVisibility).filter((status) => this.statusVisibility[status]).join('-') || 'none'; },
    groupByColumn: function () { return this.isFleet && this.groupByVehicle ? 'Vehicle Group' : ''; },
    visibleRows: function () {
      let rows = this.rows;
      if (this.isWorkshopLink) {
        const currentJobNo = String(this.jobNo || '').trim();
        rows = rows.filter((row) => {
          const linkedJobNo = this.todoJobNo(row);
          const attachedToCurrentJob = currentJobNo && linkedJobNo == currentJobNo;
          return !linkedJobNo || attachedToCurrentJob || this.showAttachedTasksValue;
        });
      }
      if (this.showStatusCounts) {
        rows = rows.filter((row) => this.statusVisibility[this.todoStatus(row)] !== false);
      }
      if (this.isFleet && !this.localShowCancelledTasks) rows = rows.filter((row) => this.todoStatus(row) != 'Cancelled');
      return rows;
    },
    displayRows: function () {
      return this.visibleRows.map((row, index) => ({ ...row, '#': index + 1 }));
    },
    activeFilterCount: function () {
      return this.selectedVehicles.length
        + (this.teamFilter && this.teamFilter.value ? 1 : 0)
        + (this.categoryFilter && this.categoryFilter.value ? 1 : 0)
        + (this.statusFilter && this.statusFilter.value && this.statusFilter.value != 'All' ? 1 : 0)
        + (this.dateFrom || this.dateTo ? 1 : 0);
    },
    filterButtonText: function () {
      return this.activeFilterCount > 0 ? `Change Filter (${this.activeFilterCount})` : 'Change Filter';
    },
    teamFilterItems: function () {
      const values = [...new Set(this.vehicleSelectorItems.map((item) => item['Team']).filter(Boolean))].sort();
      return [{ label: 'All Teams', value: '' }].concat(values.map((value) => ({ label: value, value: value })));
    },
    categoryFilterItems: function () {
      const values = [];
      const seen = {};
      this.vehicleSelectorItems.forEach((item) => {
        const code = String(item._vehicleCategoryCode || item['Vehicle Category'] || '').trim();
        const label = String(item['Vehicle Category'] || code).trim();
        if (code && !seen[code]) {
          seen[code] = true;
          values.push({ label: label, value: code });
        }
      });
      values.sort((left, right) => left.label.localeCompare(right.label));
      return [{ label: 'All Categories', value: '' }].concat(values);
    },
    statusFilterItems: function () {
      return [
        { label: 'All', value: 'All' },
        { label: 'Pending', value: 'Outstanding' },
        { label: 'Completed', value: 'Completed' },
        { label: 'Cancelled', value: 'Cancelled' },
      ];
    },
    dateFieldItems: function () {
      return [
        { label: 'Due Date', value: 'DueDate' },
        { label: 'Completion Date', value: 'CompletionDate' },
      ];
    },
    tableDataType: function () {
      return {
        '#': 'number',
        'Task Name': 'string',
        Vehicle: 'string',
        'Date Created': 'date',
        'Due Date': 'date',
        Status: 'string',
        'Completion Date': 'date',
        Remarks: 'string',
        'Vehicle Team': 'string',
        'Vehicle Category': 'string',
        'Workshop Job No': 'string',
        'Vehicle No.': 'string',
        'Vehicle Code': 'string',
        'Job No.': 'string',
        'Vehicle Group': 'string',
        _status: 'string',
      };
    },
    reviewDataType: function () {
      return {
        '#': 'number',
        Result: 'string',
        'Vehicle No.': 'string',
        'Task Name': 'string',
        Details: 'string',
      };
    },
    hiddenColumns: function () {
      const columns = ['Todo ID', '_jobNo', '_recordState', '_status', '_jobNoMissing', 'Vehicle No.', 'Vehicle Code', 'Job No.', 'Vehicle Group'];
      if (!this.isFleet) columns.push('Vehicle', 'Vehicle Team', 'Vehicle Category');
      if (this.isFleet && this.groupByVehicle) columns.push('Vehicle', 'Vehicle Team', 'Vehicle Category');
      return columns;
    },
    todoTableAdditionalButtons: function () {
      return this.isWorkshopLink && !this.deferLinking
        ? []
        : [{ text: this.viewOnly ? 'View' : 'Edit', index: 0, width: 60 }];
    },
    cancelButtons: function () {
      return [{ text: 'Delete Task', color: 'red', textColor: 'white' }, { text: 'Close', color: 'lightgray' }];
    },
    completionButtons: function () {
      return [
        { text: 'Mark Completed', disabled: this.completionActionLoading },
        { text: 'Cancel', color: 'lightgray', disabled: this.completionActionLoading },
      ];
    },
  },
  watch: {
    filterOpen: function (value) {
      if (value && this.isFleet) this.syncFilterDrafts();
    },
    vehicleNo: function () {
      if (!this.isFleet && !this.isDraftMode) this.refreshRows();
    },
    showAttachedTasks: function () {
      if (this.isWorkshopLink) this.refreshWorkshopLinkTable();
    },
    showCancelledTasks: function () {
      if (this.isWorkshopLink) {
        this.$set(this.statusVisibility, 'Cancelled', this.showCancelledTasks);
        this.refreshWorkshopLinkTable();
      }
    },
    draftItems: {
      deep: true,
      handler: function (value) {
        if (this.isDraftMode) this.replaceRows(value || []);
      },
    },
    selectedRows: {
      deep: true,
      handler: function (value) {
        if (this.isWorkshopLink) {
          this.$emit('linkSelectionChanged', (value || []).map((row) => ({
            'Todo ID': row['Todo ID'],
            'Vehicle No.': row['Vehicle No.'],
          })));
        }
      },
    },
  },
  async mounted() {
    await this.initialize();
  },
  methods: {
    async initialize() {
      if (this.isFleet) {
        this.statusVisibility = { Pending: true, Completed: false, Cancelled: false };
      } else if (this.isWorkshopLink && this.deferLinking) {
        this.statusVisibility = { Pending: true, Completed: true, Cancelled: !!this.showCancelledTasks };
      } else {
        this.statusVisibility = { Pending: true, Completed: true, Cancelled: false };
      }
      if (!this.isWorkshopLink) await this.loadColumnOrder();
      if (!this.isDraftMode) await this.loadVehicleCategories();
      if (this.isFleet) await this.loadVehicleSelector();
      if (!this.isDraftMode) await this.refreshRows();
      else await this.replaceRows(this.draftItems);
    },
    normalizeVehicle: function (item) {
      const source = item || {};
      const inventoryNo = String(source['Inventory #'] || source['Vehicle No.'] || source['Lorry No.'] || source.Lorry_No || '').trim();
      const inventoryCode = String(source['Inventory Code'] || source['Vehicle Code'] || source.VehicleCode || '').trim();
      const team = String(source.Team || source['Vehicle Team'] || '').trim();
      const categoryCode = String(source._vehicleCategoryCode || source['Vehicle Category'] || source.Category || source.VehicleCategory || '').trim();
      return {
        ...source,
        'Inventory #': inventoryNo,
        'Inventory Code': inventoryCode,
        Team: team,
        'Vehicle Category': this.vehicleCategoryDisplay(categoryCode),
        _vehicleCategoryCode: categoryCode,
        label: `${inventoryNo}${inventoryCode ? ` - ${inventoryCode}` : ''}`,
      };
    },
    todoStatus(row) {
      return String(row && (row._status || row.Status) || '').replace(/<[^>]*>/g, '').trim();
    },
    statusDisplay(status) {
      const normalizedStatus = String(status || '').trim();
      const color = normalizedStatus == 'Completed' ? '#16a34a' : normalizedStatus == 'Cancelled' ? '#ef4444' : '#f59e0b';
      return `<span style="display:inline-flex;align-items:center;gap:5px"><span aria-hidden="true" style="width:9px;height:9px;border-radius:50%;display:inline-block;background-color:${color}"></span>${normalizedStatus}</span>`;
    },
    normalizeRows: function (items) {
      if (!Array.isArray(items)) return [];
      return items.map((item, index) => {
        const source = item || {};
        const recordState = String(source['_recordState'] || source.RecordState || 'A').trim().toUpperCase() == 'C' ? 'C' : 'A';
        const completionDate = source['Completion Date'] || source.CompletionDate || source.completiondate || '';
        const status = recordState == 'C' ? 'Cancelled' : (completionDate ? 'Completed' : 'Pending');
        const linkedJobNo = this.todoJobNo(source);
        const jobNoMissing = String(source['_jobNoMissing'] || source.JobNoMissing || '').trim() == '1';
        const vehicleNo = String(source['Vehicle No.'] || source['Inventory #'] || source.InventoryNo || source.inventoryno || '').trim();
        const vehicleCode = String(source['Vehicle Code'] || source['Inventory Code'] || source.InventoryCode || '').trim();
        const vehicleTeam = String(source['Vehicle Team'] || source.Team || '').trim();
        const vehicleCategory = this.vehicleCategoryDisplay(String(source['Vehicle Category'] || source.Category || '').trim());
        const taskName = String(source['Task Name'] || source.TaskName || source.taskname || '').trim();
        const vehicleDisplay = `${vehicleNo}${vehicleCode ? ` - ${vehicleCode}` : ''}`;
        const workshopJobDisplay = jobNoMissing && linkedJobNo ? `${linkedJobNo} (Unavailable)` : linkedJobNo;
        const normalizedRow = {
          '#': index + 1,
          'Task Name': taskName,
          Vehicle: vehicleDisplay,
          'Date Created': source['Date Created'] || source.DateCreated || source.datecreated || '',
          'Due Date': source['Due Date'] || source.DueDate || source.duedate || '',
          Status: this.statusDisplay(status),
          _status: status,
          'Completion Date': completionDate,
          Remarks: String(source.Remarks || source.remarks || '').trim(),
          'Vehicle Team': vehicleTeam,
          'Vehicle Category': vehicleCategory,
          'Workshop Job No': workshopJobDisplay,
          'Todo ID': source['Todo ID'] || source.TodoID || source.todoid || '',
          'Vehicle No.': vehicleNo,
          'Vehicle Code': vehicleCode,
          'Job No.': workshopJobDisplay,
          'Vehicle Group': [
            `Vehicle: ${vehicleDisplay}`,
            vehicleCategory ? `Category: ${vehicleCategory}` : '',
            vehicleTeam ? `Team: ${vehicleTeam}` : '',
          ].filter(Boolean).join(', '),
          _rowid: source['_rowid'] || `row${index}`,
          _jobNo: linkedJobNo,
          _jobNoMissing: jobNoMissing ? '1' : '0',
          _recordState: recordState,
        };
        if (source._draftAction) normalizedRow._draftAction = source._draftAction;
        normalizedRow['#'] = index + 1;
        return normalizedRow;
      });
    },
    async loadVehicleSelector() {
      const result = await this.$apiCallV2('/Vehicle/GetVehicleSelectorList', {});
      this.vehicleSelectorItems = Array.isArray(result) ? result.map(this.normalizeVehicle) : [];
    },
    async loadVehicleCategories() {
      try {
        const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VEHICLECATEGORY_UNIQUE' });
        this.vehicleCategoryItems = Array.isArray(result) ? result : [];
      } catch (_error) {
        this.vehicleCategoryItems = [];
      }
    },
    async loadColumnOrder() {
      if (this.isWorkshopLink || this.columnOrderID !== null) return;

      const columnOrderID = {
        formname: 'EMS_Vehicle',
        formctrlname: 'Vehicle Tasks',
      };

      try {
        const result = await this.$apiCallV2('/TableBox/GetColumnOrder', columnOrderID);
        this.columnOrderViews = Array.isArray(result) ? result : [];
        this.columnOrderID = columnOrderID;
      } catch (_error) {
        this.columnOrderViews = [];
      }
    },
    vehicleCategoryDisplay: function (categoryCode) {
      const code = String(categoryCode || '').trim();
      if (!code) return '';
      const category = this.vehicleCategoryItems.find((item) => {
        const itemCode = String(item['Category Code'] || item.CategoryCode || item.Category || '').trim();
        return itemCode.toUpperCase() == code.toUpperCase();
      });
      const description = category
        ? String(category.Description || category.CategoryDesc || category['Category Description'] || '').trim()
        : '';
      return description && description.toUpperCase() != code.toUpperCase() ? `${code} - ${description}` : code;
    },
    syncFilterDrafts() {
      this.draftSelectedVehicles = this.selectedVehicles.slice();
      this.draftTeamFilter = this.teamFilter ? { ...this.teamFilter } : null;
      this.draftCategoryFilter = this.categoryFilter ? { ...this.categoryFilter } : null;
      this.draftStatusFilter = this.statusFilter ? { ...this.statusFilter } : { label: 'All', value: 'All' };
      this.draftDateField = this.dateField ? { ...this.dateField } : { label: 'Due Date', value: 'DueDate' };
      this.draftDateFrom = this.dateFrom;
      this.draftDateTo = this.dateTo;
    },
    applyFilterChanges() {
      this.selectedVehicles = this.draftSelectedVehicles.slice();
      this.teamFilter = this.draftTeamFilter ? { ...this.draftTeamFilter } : null;
      this.categoryFilter = this.draftCategoryFilter ? { ...this.draftCategoryFilter } : null;
      this.statusFilter = this.draftStatusFilter ? { ...this.draftStatusFilter } : { label: 'All', value: 'All' };
      this.dateField = this.draftDateField ? { ...this.draftDateField } : { label: 'Due Date', value: 'DueDate' };
      this.dateFrom = this.draftDateFrom;
      this.dateTo = this.draftDateTo;
      this.filterOpen = false;
      this.refreshRows();
    },
    showCancelledTasksChanged() {
      if (!this.isWorkshopLink) this.$set(this.statusVisibility, 'Cancelled', this.localShowCancelledTasks);
      this.$nextTick(() => {
        if (this.isWorkshopLink) this.rows = this.rows.slice();
        else this.refreshRows();
      });
    },
    todoSelectionKey(row) {
      const todoID = String(row && row['Todo ID'] || '').trim();
      return todoID ? `todo:${todoID}` : `row:${String(row && row._rowid || '').trim()}`;
    },
    toggleStatusVisibility(status) {
      const nextValue = !this.statusVisibility[status];
      const selectedKeys = new Set(
        this.selectedRows
          .filter((row) => nextValue || this.todoStatus(row) != status)
          .map((row) => this.todoSelectionKey(row)),
      );
      this.$set(this.statusVisibility, status, nextValue);
      if (status == 'Cancelled') {
        if (this.isWorkshopLink) this.$emit('update:showCancelledTasks', nextValue);
        else this.localShowCancelledTasks = nextValue;
      }
      if (this.isWorkshopLink) {
        this.refreshWorkshopLinkTable(new Set(this.selectedRows.map((row) => String(row['Todo ID']))));
      } else {
        this.$nextTick(async () => {
          this.rows = this.rows.slice();
          await this.$nextTick();
          this.selectedRows = this.rows.filter((row) => selectedKeys.has(this.todoSelectionKey(row)));
        });
      }
    },
    async refreshWorkshopLinkTable(selectedIDs) {
      const preservedIDs = selectedIDs || new Set(this.selectedRows.map((row) => String(row['Todo ID'])));
      await this.$nextTick();
      this.rows = this.rows.slice();
      await this.$nextTick();
      this.selectedRows = this.rows.filter((row) => preservedIDs.has(String(row['Todo ID'])));
    },
    buildListRequest() {
      const request = { status: this.statusFilter ? this.statusFilter.value : 'All' };
      if (this.isWorkshopLink || (!this.isFleet && this.vehicleNo)) request.vehiclenos = [this.vehicleNo];
      if (this.isFleet && this.selectedVehicles.length > 0) request.vehiclenos = this.selectedVehicles.map((item) => item['Inventory #']);
      if (this.isFleet && this.teamFilter && this.teamFilter.value) request.teams = [this.teamFilter.value];
      if (this.isFleet && this.categoryFilter && this.categoryFilter.value) request.categories = [this.categoryFilter.value];
      if (this.isFleet && this.dateField && this.dateField.value) request.datefield = this.dateField.value;
      if (this.isFleet && this.dateFrom) request.startdate = formatDate(this.dateFrom);
      if (this.isFleet && this.dateTo) request.enddate = formatDate(this.dateTo);
      return request;
    },
    async refreshRows() {
      if (this.isDraftMode) {
        await this.replaceRows(this.draftItems);
        this.selectedRows = [];
        return;
      }
      if (!this.isFleet && !this.vehicleNo) {
        this.rows = [];
        return;
      }
      this.loading = true;
      this.selectedRows = [];
      try {
        const result = await this.$apiCallV2('/Vehicle/GetVehicleTodoList', this.buildListRequest());
        await this.replaceRows(result);
        if (this.isWorkshopLink && this.deferLinking) {
          const deferredChanges = Array.isArray(this.deferredTodoUpdates) ? this.deferredTodoUpdates : [];
          this.deferredTodoUpdateRows = deferredChanges.filter((row) => String(row.action || 'E').toUpperCase() != 'N').map((row) => ({ ...row }));
          this.deferredTodoCreateRows = deferredChanges.filter((row) => String(row.action || '').toUpperCase() == 'N').map((row) => ({ ...row }));
          this.deferredTodoReactivationRows = deferredChanges.filter((row) => String(row.action || '').toUpperCase() == 'R').map((row) => ({ ...row }));
          this.deferredTodoUpdateRows = this.deferredTodoUpdateRows.filter((row) => String(row.action || '').toUpperCase() != 'R');
          this.applyDeferredTodoUpdates();
          await this.$nextTick();
          await this.initializeDeferredLinking();
        }
      } finally {
        this.loading = false;
      }
    },
    async replaceRows(items) {
      const normalizedRows = this.normalizeRows(items);
      this.rows = [];
      await this.$nextTick();
      this.rows = normalizedRows;
    },
    applyDeferredTodoUpdates() {
      if (!this.isWorkshopLink || !this.deferLinking || (this.deferredTodoUpdateRows.length == 0 && this.deferredTodoCreateRows.length == 0 && this.deferredTodoReactivationRows.length == 0)) return;
      const reactivationIDs = new Set(this.deferredTodoReactivationRows.map((row) => String(row.todoid)));
      const updates = {};
      this.deferredTodoUpdateRows.forEach((update) => {
        updates[String(update.todoid)] = update;
      });
      this.rows = this.rows.map((row) => {
        const reactivatedRow = reactivationIDs.has(String(row['Todo ID']))
          ? this.mergeDeferredTodoRow(row, { action: 'R', completiondate: '', jobno: this.todoJobNo(row) })
          : row;
        const update = updates[String(row['Todo ID'])];
        return update ? this.mergeDeferredTodoRow(reactivatedRow, update) : reactivatedRow;
      });
      const existingIDs = new Set(this.rows.map((row) => String(row['Todo ID'])));
      const newRows = this.deferredTodoCreateRows
        .filter((row) => !existingIDs.has(String(row.todoid)))
        .map((row) => this.deferredTodoRowFromPayload(row));
      if (newRows.length > 0) this.rows = this.rows.concat(newRows);
    },
    deferredTodoRowFromPayload(payload) {
      const row = this.normalizeRows([{
        'Todo ID': payload.todoid,
        'Vehicle No.': payload.inventoryno,
        'Task Name': payload.taskname,
        'Due Date': payload.duedate,
        'Completion Date': payload.completiondate,
        Remarks: payload.remarks,
        'Workshop Job No': payload.jobno || '',
        'Job No.': payload.jobno || '',
        _jobNo: payload.jobno || '',
      }])[0];
      row._rowid = `deferred-${String(payload.todoid).replace(/[^A-Za-z0-9_-]/g, '_')}`;
      return row;
    },
    mergeDeferredTodoRow(row, update) {
      const hasValue = (key) => Object.prototype.hasOwnProperty.call(update, key);
      const completionDate = hasValue('completiondate') ? update.completiondate : row['Completion Date'];
      const jobNo = hasValue('jobno') ? String(update.jobno || '').trim() : this.todoJobNo(row);
      const recordState = String(update.action || '').toUpperCase() == 'R' ? 'A' : row['_recordState'];
      const status = recordState == 'C' ? 'Cancelled' : completionDate ? 'Completed' : 'Pending';
      return {
        ...row,
        'Task Name': hasValue('taskname') ? update.taskname : row['Task Name'],
        'Due Date': hasValue('duedate') ? update.duedate : row['Due Date'],
        Status: this.statusDisplay(status),
        _status: status,
        'Completion Date': completionDate,
        Remarks: hasValue('remarks') ? update.remarks : row.Remarks,
        'Workshop Job No': jobNo,
        'Job No.': jobNo,
        _jobNo: jobNo,
        _recordState: recordState,
      };
    },
    stageDeferredTodoChange(payload) {
      const payloadID = String(payload.todoid);
      if (String(payload.action || '').toUpperCase() == 'R') {
        this.deferredTodoReactivationRows = this.deferredTodoReactivationRows
          .filter((row) => String(row.todoid) != payloadID)
          .concat(payload);
        return;
      }
      if (String(payload.action || '').toUpperCase() == 'N') {
        this.deferredTodoCreateRows = this.deferredTodoCreateRows
          .filter((row) => String(row.todoid) != payloadID)
          .concat(payload);
        return;
      }
      const createIndex = this.deferredTodoCreateRows.findIndex((row) => String(row.todoid) == payloadID);
      if (createIndex >= 0) {
        this.deferredTodoCreateRows.splice(createIndex, 1, {
          ...this.deferredTodoCreateRows[createIndex],
          ...payload,
          action: 'N',
        });
        return;
      }
      this.deferredTodoUpdateRows = this.deferredTodoUpdateRows
        .filter((row) => String(row.todoid) != payloadID)
        .concat(payload);
    },
    async initializeDeferredLinking() {
      if (!this.isWorkshopLink || !this.deferLinking) return;
      const currentJobNo = String(this.jobNo || '').trim();
      const linkedRows = currentJobNo
        ? this.rows.filter((row) => this.todoJobNo(row) == currentJobNo)
        : [];
      this.deferredOriginalTodoIDs = linkedRows.map((row) => String(row['Todo ID']));
      if (this.viewOnly) {
        this.selectedRows = [];
      } else if (Array.isArray(this.deferredSelectedTodoIDs)) {
        const selectedIDs = new Set(this.deferredSelectedTodoIDs.map((todoID) => String(todoID)));
        this.selectedRows = this.rows.filter((row) => selectedIDs.has(String(row['Todo ID'])));
      } else {
        this.selectedRows = linkedRows;
      }
      await this.$nextTick();
      await this.$nextTick();
      this.selectedRows = this.selectedRows.slice();
    },
    todoJobNo(row) {
      const source = row || {};
      return String(source['_jobNo'] || source['Workshop Job No'] || source['Job No.'] || source['Job No'] || source.JobNo || source.jobno || '').trim();
    },
    getDeferredLinkChanges() {
      if (!this.isWorkshopLink || !this.deferLinking) return { toLink: [], toUnlink: [] };
      const originalIDs = new Set(this.deferredOriginalTodoIDs);
      const createIDs = new Set(this.deferredTodoCreateRows.map((row) => String(row.todoid)));
      const selectedIDs = new Set(this.selectedRows.map((row) => String(row['Todo ID'])));
      return {
        toLink: this.selectedRows.filter((row) => !originalIDs.has(String(row['Todo ID'])) && !createIDs.has(String(row['Todo ID']))),
        toUnlink: this.rows.filter((row) => originalIDs.has(String(row['Todo ID'])) && !selectedIDs.has(String(row['Todo ID']))),
      };
    },
    getDeferredTodoChanges() {
      if (!this.isWorkshopLink || !this.deferLinking) return [];
      return this.deferredTodoReactivationRows.concat(this.deferredTodoCreateRows, this.deferredTodoUpdateRows).map((row) => ({ ...row }));
    },
    clearFilters() {
      this.draftSelectedVehicles = [];
      this.draftTeamFilter = null;
      this.draftCategoryFilter = null;
      this.draftStatusFilter = { label: 'All', value: 'All' };
      this.draftDateField = { label: 'Due Date', value: 'DueDate' };
      this.draftDateFrom = null;
      this.draftDateTo = null;
      this.applyFilterChanges();
    },
    async editorVehiclesSelected(items) {
      this.editorVehicles = (Array.isArray(items) ? items : []).map(this.normalizeVehicle);
      this.editorJob = null;
      this.editorJobItems = this.editorVehicles.length == 1 && !this.isDraftMode
        ? await this.getWorkshopJobs(this.editorVehicles[0]['Inventory #'])
        : [];
    },
    todoTableButtonClicked: function (_buttonText, row) {
      if (!row) return;
      if (!(this.isWorkshopLink && this.deferLinking)) this.selectedRows = [row];
      this.openEdit(row);
    },
    openAdd() {
      this.editorAction = 'Add';
      this.editorViewOnly = false;
      this.editorTaskName = '';
      this.editorTaskNameError = '';
      this.editorDueDate = null;
      this.editorCompleted = false;
      this.editorCompletionDate = null;
      this.editorRemarks = '';
      this.editorJob = null;
      this.editorJobItems = [];
      this.editorRow = null;
      if (this.isFleet) {
        this.editorVehicles = this.selectedVehicles.length > 0 ? this.selectedVehicles.slice() : [];
      } else if (this.vehicleNo) {
        this.editorVehicles = [{ 'Inventory #': this.vehicleNo, 'Inventory Code': '' }];
      } else if (this.isDraftMode) {
        this.editorVehicles = [{ 'Inventory #': '', 'Inventory Code': '' }];
      } else {
        this.editorVehicles = [];
      }
      this.editorShow = true;
      if (this.editorVehicles.length == 1 && !this.isDraftMode) {
        const inventoryNo = this.editorVehicles[0]['Inventory #'];
        this.getWorkshopJobs(inventoryNo).then((items) => {
          if (this.editorShow && this.editorAction == 'Add' && this.editorVehicles.length == 1 && this.editorVehicles[0]['Inventory #'] == inventoryNo) {
            this.editorJobItems = items;
          }
        }).catch(() => {});
      }
    },
    openEdit(row) {
      const selectedRow = row || this.selectedRow;
      if (!selectedRow) return;
      this.editorAction = 'Edit';
      this.editorViewOnly = !this.canEdit;
      this.editorRow = selectedRow;
      this.editorTaskName = selectedRow['Task Name'];
      this.editorTaskNameError = '';
      this.editorDueDate = parseDate(selectedRow['Due Date']);
      this.editorCompleted = this.todoStatus(selectedRow) == 'Completed';
      this.editorCompletionDate = parseDate(selectedRow['Completion Date']);
      this.editorRemarks = selectedRow.Remarks || '';
      this.editorJob = null;
      this.editorVehicles = [{ 'Inventory #': selectedRow['Vehicle No.'], 'Inventory Code': selectedRow['Vehicle Code'] }];
      this.editorJobItems = [];
      this.editorShow = true;
      const inventoryNo = selectedRow['Vehicle No.'];
      this.getWorkshopJobs(inventoryNo).then((items) => {
        if (this.editorShow && this.editorAction == 'Edit' && this.editorRow == selectedRow) {
          this.editorJobItems = items;
          this.editorJob = items.find((item) => item['Job #'] == this.todoJobNo(selectedRow)) || null;
        }
      }).catch(() => {});
    },
    openCompletionPopup() {
      if (this.completionActionLoading || !this.allSelectedRowsOutstanding || !this.canEdit) return;
      this.completionDate = new Date();
      this.completionDateError = '';
      this.completionPopupShow = true;
    },
    completionActionClicked() {
      if (this.allSelectedRowsCompleted) this.markUncompleted();
      else this.openCompletionPopup();
    },
    async completionButtonClicked(button) {
      if (button == 'Cancel') {
        this.completionPopupShow = false;
        return;
      }
      this.completionDateError = '';
      if (!this.completionDate) {
        this.completionDateError = 'Completion Date is required';
        return;
      }

      if (this.completionActionLoading) return;
      this.completionActionLoading = true;
      this.$fullscreenLoading('Marking Tasks Completed');
      try {
        const completionDate = formatDate(this.completionDate);
        const failures = await this.saveSelectedCompletionDate(completionDate);
        this.completionPopupShow = false;
        if (failures === null) {
          if (!(this.isWorkshopLink && this.deferLinking)) this.selectedRows = [];
          return;
        }
        if (failures.length > 0) {
          this.$showAlert('error', 'Tasks Could Not Be Completed', failures.map((row) => row.ErrMsg || row.Details || 'Complete failed').join('<br>'), [{ text: 'OK' }]);
        }
        await this.refreshRows();
        this.selectedRows = [];
      } finally {
        this.completionActionLoading = false;
        this.$fullscreenLoading();
      }
    },
    async markUncompleted() {
      if (this.completionActionLoading || !this.allSelectedRowsCompleted || !this.canEdit) return;
      this.completionActionLoading = true;
      let fullscreenLoadingShown = false;
      try {
        const confirm = await this.$showAlert('warning', 'Mark as Uncompleted', `Mark ${this.selectedRows.length} selected task${this.selectedRows.length == 1 ? '' : 's'} as uncompleted?`, [
          { text: 'Mark as Uncompleted' },
          { text: 'Cancel', color: 'lightgray' },
        ]);
        if (confirm == 'Cancel') return;

        this.$fullscreenLoading('Marking Tasks Uncompleted');
        fullscreenLoadingShown = true;
        const failures = await this.saveSelectedCompletionDate('');
        if (failures === null) {
          if (!(this.isWorkshopLink && this.deferLinking)) this.selectedRows = [];
          return;
        }
        if (failures.length > 0) {
          this.$showAlert('error', 'Tasks Could Not Be Marked Uncompleted', failures.map((row) => row.ErrMsg || row.Details || 'Update failed').join('<br>'), [{ text: 'OK' }]);
        }
        await this.refreshRows();
        this.selectedRows = [];
      } finally {
        if (fullscreenLoadingShown) this.$fullscreenLoading();
        this.completionActionLoading = false;
      }
    },
    async saveSelectedCompletionDate(completionDate) {
      if (this.isDraftMode) {
        const selectedIDs = this.selectedRows.map((row) => row['Todo ID']);
        const draftRows = this.normalizeRows(this.draftItems).map((row) => selectedIDs.includes(row['Todo ID']) ? {
          ...row,
          Status: this.statusDisplay(completionDate ? 'Completed' : 'Pending'),
          _status: completionDate ? 'Completed' : 'Pending',
          'Completion Date': completionDate,
          _draftAction: row._draftAction || 'E',
        } : row);
        this.$emit('draftChanged', draftRows);
        await this.replaceRows(draftRows);
        return null;
      }

      const todos = this.selectedRows.map((row) => ({
        action: 'E',
        todoid: row['Todo ID'],
        inventoryno: row['Vehicle No.'],
        taskname: row['Task Name'],
        duedate: formatDate(row['Due Date']),
        completiondate: completionDate,
        remarks: row.Remarks || '',
        jobno: row._jobNo || row['Job No.'] || '',
      }));
      if (this.isWorkshopLink && this.deferLinking) {
        const selectedIDs = new Set(this.selectedRows.map((row) => String(row['Todo ID'])));
        todos.forEach((todo) => this.stageDeferredTodoChange(todo));
        const updatedRows = this.rows.map((row) => {
          const todo = todos.find((item) => String(item.todoid) == String(row['Todo ID']));
          return todo ? this.mergeDeferredTodoRow(row, todo) : row;
        });
        this.rows = [];
        await this.$nextTick();
        this.rows = updatedRows;
        await this.$nextTick();
        this.selectedRows = this.rows.filter((row) => selectedIDs.has(String(row['Todo ID'])));
        return null;
      }
      const result = await this.$apiCallV2('/Vehicle/SaveVehicleTodo', { todos: todos });
      return (Array.isArray(result) ? result : []).filter((row) => row.Results == '1' || row.Result == 'Failed');
    },
    editorCompletionChanged(value) {
      this.editorCompleted = value;
      this.editorCompletionDate = value ? (this.editorCompletionDate || new Date()) : null;
    },
    async getWorkshopJobs(inventoryNo) {
      if (!inventoryNo) return [];
      const result = await this.$apiCallV2('/WorkshopJob/GetEMSJobList', { startdate: '', enddate: '', inventoryno: inventoryNo });
      return (Array.isArray(result) ? result : []).map((item) => {
        const jobNo = item['Job #'] || item.JobNo || '';
        const jobDate = item['Job Date'] || item.JobDate || '';
        return { ...item, label: jobDate ? `${jobNo} (${jobDate})` : jobNo };
      });
    },
    cancelEditor() {
      if (this.editorSaveLoading) return;
      this.editorShow = false;
      this.editorRow = null;
      this.editorTaskNameError = '';
    },
    async reactivateEditorTask() {
      if (this.editorSaveLoading || !this.editorRow || !this.editorTaskIsCancelled || !this.canEdit) return;
      const payload = {
        action: 'R',
        todoid: this.editorRow['Todo ID'],
        inventoryno: this.editorRow['Vehicle No.'],
      };

      this.editorSaveLoading = true;
      try {
        if (this.isDraftMode) {
          const draftRows = this.normalizeRows(this.draftItems).map((row) => String(row['Todo ID']) == String(payload.todoid)
            ? {
                ...row,
                Status: this.statusDisplay('Pending'),
                _status: 'Pending',
                'Completion Date': '',
                _recordState: 'A',
                _draftAction: row._draftAction || 'R',
              }
            : row);
          this.$emit('draftChanged', draftRows);
          await this.replaceRows(draftRows);
          const updatedRow = this.rows.find((row) => String(row['Todo ID']) == String(payload.todoid));
          if (updatedRow) {
            this.editorRow = updatedRow;
            this.editorCompleted = false;
            this.editorCompletionDate = null;
            this.selectedRows = [updatedRow];
          }
          return;
        }
        if (this.isWorkshopLink && this.deferLinking) {
          this.stageDeferredTodoChange(payload);
          const updatedRows = this.rows.map((row) => String(row['Todo ID']) == String(payload.todoid)
            ? this.mergeDeferredTodoRow(row, { ...payload, completiondate: '', jobno: this.todoJobNo(row) })
            : row);
          this.rows = [];
          await this.$nextTick();
          this.rows = updatedRows;
          await this.$nextTick();
          const updatedRow = this.rows.find((row) => String(row['Todo ID']) == String(payload.todoid));
          if (updatedRow) {
            this.editorRow = updatedRow;
            this.editorCompleted = false;
            this.editorCompletionDate = null;
            this.selectedRows = this.selectedRows.map((row) => String(row['Todo ID']) == String(payload.todoid) ? updatedRow : row);
          }
          return;
        }

        this.$fullscreenLoading('Reinstating Task');
        const result = await this.$apiCallV2('/Vehicle/SaveVehicleTodo', { todos: [payload] });
        const failures = (Array.isArray(result) ? result : []).filter(
          (item) => item.Results == '1' || item.Result == 'Failed',
        );
        if (failures.length > 0) {
          this.$showAlert('error', 'Task Could Not Be Reinstated', failures.map((item) => item.ErrMsg || 'Reinstate failed').join('<br>'), [{ text: 'OK' }]);
          return;
        }
        await this.refreshRows();
        const updatedRow = this.rows.find((row) => String(row['Todo ID']) == String(payload.todoid));
        if (updatedRow) {
          this.selectedRows = [updatedRow];
          this.openEdit(updatedRow);
        }
      } finally {
        this.editorSaveLoading = false;
        if (!(this.isWorkshopLink && this.deferLinking)) this.$fullscreenLoading();
      }
    },
    buildTodoPayload(vehicle, action, row) {
      const completed = this.editorCompleted;
      const existingJobNo = this.editorRow ? this.todoJobNo(this.editorRow) : '';
      return {
        action: action,
        todoid: row ? row['Todo ID'] : '',
        inventoryno: vehicle['Inventory #'] || this.vehicleNo,
        taskname: this.editorTaskName.trim(),
        duedate: formatDate(this.editorDueDate),
        completiondate: completed ? formatDate(this.editorCompletionDate || new Date()) : '',
        remarks: this.editorRemarks || '',
        jobno: this.editorJob ? (this.editorJob['Job #'] || this.editorJob.JobNo || '') : existingJobNo,
      };
    },
    async saveEditor() {
      if (this.editorSaveLoading) return;
      this.editorSaveLoading = true;
      this.$fullscreenLoading('Saving Task');
      try {
        await this.saveEditorInternal();
      } finally {
        this.editorSaveLoading = false;
        this.$fullscreenLoading();
      }
    },
    async saveEditorInternal() {
      this.editorTaskNameError = '';
      if (!this.editorTaskName || !this.editorTaskName.trim()) {
        this.editorTaskNameError = 'Task Name is required';
        return;
      }
      if (this.editorVehicles.length == 0) {
        this.editorTaskNameError = 'Select at least one vehicle';
        return;
      }

      if (this.isDraftMode) {
        const draftRows = this.normalizeRows(this.draftItems).slice();
        if (this.editorAction == 'Add') {
          this.editorVehicles.forEach((vehicle) => draftRows.push({
            ...this.buildTodoPayload(vehicle, 'N'),
            'Todo ID': `draft-${Date.now()}-${draftRows.length}`,
            'Vehicle No.': vehicle['Inventory #'],
            'Vehicle Code': vehicle['Inventory Code'] || '',
            'Task Name': this.editorTaskName.trim(),
            'Due Date': formatDate(this.editorDueDate),
            Status: this.statusDisplay(this.editorCompleted ? 'Completed' : 'Pending'),
            _status: this.editorCompleted ? 'Completed' : 'Pending',
            'Completion Date': this.editorCompleted ? formatDate(this.editorCompletionDate || new Date()) : '',
            Remarks: this.editorRemarks || '',
            'Job No.': '',
            _recordState: 'A',
            _draftAction: 'N',
          }));
        } else if (this.editorRow) {
          const index = draftRows.findIndex((row) => row['Todo ID'] == this.editorRow['Todo ID']);
          if (index >= 0) draftRows[index] = { ...draftRows[index], ...this.normalizeRows([this.buildTodoPayload(this.editorVehicles[0], 'E', this.editorRow)])[0], _draftAction: 'E' };
        }
        this.$emit('draftChanged', draftRows);
        await this.replaceRows(draftRows);
        this.editorShow = false;
        return;
      }

      const payloadRows = this.editorAction == 'Add'
        ? this.editorVehicles.map((vehicle) => this.buildTodoPayload(vehicle, 'N'))
        : [this.buildTodoPayload(this.editorVehicles[0], 'E', this.editorRow)];
      if (this.isWorkshopLink && this.deferLinking) {
        let payload = payloadRows[0];
        if (this.editorAction == 'Add') {
          payload = {
            ...payload,
            action: 'N',
            todoid: `draft-${Date.now()}-${this.deferredTodoCreateRows.length}`,
            jobno: '',
          };
        }
        this.stageDeferredTodoChange(payload);
        const selectedIDs = new Set(this.selectedRows.map((row) => String(row['Todo ID'])));
        const updatedRows = this.rows.slice();
        const existingRowIndex = updatedRows.findIndex((row) => String(row['Todo ID']) == String(payload.todoid));
        if (existingRowIndex >= 0) updatedRows.splice(existingRowIndex, 1, this.mergeDeferredTodoRow(updatedRows[existingRowIndex], payload));
        else updatedRows.push(this.deferredTodoRowFromPayload(payload));
        this.rows = [];
        await this.$nextTick();
        this.rows = updatedRows;
        await this.$nextTick();
        const selectedTodoRows = this.rows.filter((row) => selectedIDs.has(String(row['Todo ID'])));
        if (this.editorAction == 'Add') selectedTodoRows.push(this.rows.find((row) => String(row['Todo ID']) == String(payload.todoid)));
        this.selectedRows = selectedTodoRows.filter(Boolean);
        this.editorShow = false;
        this.editorRow = null;
        return;
      }
      const result = await this.$apiCallV2('/Vehicle/SaveVehicleTodo', { todos: payloadRows });
      const resultRows = Array.isArray(result) ? result : [];
      const failed = resultRows.filter((row) => row.Results == '1' || row.Result == 'Failed');
      if (this.editorAction == 'Add' && payloadRows.length > 1) {
        this.reviewRows = resultRows.map((row, index) => ({
          '#': index + 1,
          Result: row.Result || (row.Results == '0' ? 'Created' : 'Failed'),
          'Vehicle No.': row['Vehicle No.'] || payloadRows[index]?.inventoryno || '',
          'Task Name': payloadRows[index]?.taskname || this.editorTaskName,
          Details: row.ErrMsg || row.Details || '',
        }));
        this.reviewPopupShow = true;
      } else if (failed.length > 0) {
        this.$showAlert('error', 'Task Could Not Be Saved', failed.map((row) => row.ErrMsg || row.Details || 'Save failed').join('<br>'), [{ text: 'OK' }]);
      }
      this.editorShow = false;
      await this.refreshRows();
    },
    cancelSelected() {
      if (!this.selectedRow || !this.selectedRowIsActive || !this.canEdit) return;
      this.cancelExistingRemarks = this.selectedRow.Remarks || '';
      this.cancelRemarks = '';
      this.cancelPopupShow = true;
    },
    async cancelButtonClicked(button) {
      if (button == 'Close') {
        this.cancelPopupShow = false;
        this.cancelExistingRemarks = '';
        return;
      }
      if (!this.cancelRemarks || !this.cancelRemarks.trim()) {
        this.$showAlert('error', 'Deletion Reason Required', 'Enter the reason in Remarks.', [{ text: 'OK' }]);
        return;
      }
      const cancellationRemarks = [this.cancelExistingRemarks, this.cancelRemarks.trim()]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join('\n');
      if (cancellationRemarks.length > 200) {
        this.$showAlert('error', 'Cancellation Reason Too Long', 'Combined Remarks cannot exceed 200 characters.', [{ text: 'OK' }]);
        return;
      }
      if (this.isDraftMode) {
        const draftRows = this.normalizeRows(this.draftItems).map((row) => row['Todo ID'] == this.selectedRow['Todo ID'] ? {
          ...row,
          Status: this.statusDisplay('Cancelled'),
          _status: 'Cancelled',
          Remarks: cancellationRemarks,
          _recordState: 'C',
          _draftAction: row._draftAction || 'C',
        } : row);
        this.$emit('draftChanged', draftRows);
        await this.replaceRows(draftRows);
      } else {
        await this.$apiCallV2('/Vehicle/SaveVehicleTodo', { todos: [{ action: 'C', todoid: this.selectedRow['Todo ID'], inventoryno: this.selectedRow['Vehicle No.'], remarks: cancellationRemarks }] });
        await this.refreshRows();
      }
      this.cancelPopupShow = false;
      this.cancelExistingRemarks = '';
      this.selectedRows = [];
    },
    async linkSelected() {
      if (!this.jobNo || this.selectedRows.length == 0 || !this.canEdit) return;
      const todos = this.selectedRows.map((row) => ({ action: 'L', todoid: row['Todo ID'], inventoryno: row['Vehicle No.'], jobno: this.jobNo }));
      await this.$apiCallV2('/Vehicle/SaveVehicleTodo', { todos: todos });
      await this.refreshRows();
      this.selectedRows = [];
    },
    async unlinkSelected() {
      if (this.selectedRows.length == 0 || !this.canEdit || (this.isWorkshopLink && !this.jobNo)) return;
      const todos = this.selectedRows.map((row) => ({ action: 'L', todoid: row['Todo ID'], inventoryno: row['Vehicle No.'], jobno: '' }));
      await this.$apiCallV2('/Vehicle/SaveVehicleTodo', { todos: todos });
      await this.refreshRows();
      this.selectedRows = [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.vehicle-todo-list {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
}

.todo-work-area {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 10px;
}

.todo-work-area > .todo-table-container:only-child {
  grid-column: 1 / -1;
}

.todo-table-container {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.todo-table-container > .table-box-main {
  flex: 1;
  min-height: 0;
}

.todo-table-options {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 4px;
}

.todo-editor-container {
  min-width: 0;
  min-height: 0;
  height: 100%;
  align-self: stretch;
  overflow-y: auto;
  padding-left: 10px;
  box-sizing: border-box;
  border-left: 1px solid #d8dee8;
}

.todo-editor-header {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 8px;
  margin-bottom: 10px;
}

.todo-form-title {
  display: inline-flex;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: bold;
}

.todo-editor-container .cta-group {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 4px;
  justify-content: flex-end;
  align-items: center;
}

.todo-toolbar,
.todo-filter-summary,
.todo-actions,
.todo-counts {
  display: flex;
  align-items: center;
}

.todo-toolbar {
  position: relative;
  z-index: 30;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 6px;
}

.todo-filter-summary {
  position: relative;
  z-index: 20;
  justify-content: flex-start;
  gap: 6px;
}

.todo-counts {
  justify-content: center;
  text-align: center;
  gap: 10px;
  color: #555;
  flex-shrink: 0;
  white-space: nowrap;
}

.todo-link-status-counts {
  gap: 14px;
}

.todo-status-count {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: transparent !important;
  outline: none;
  box-shadow: none;
  color: #222;
  cursor: pointer;
  font: inherit;
}

.todo-status-count:hover,
.todo-status-count:focus,
.todo-status-count:active {
  background: transparent !important;
  border: 0;
  outline: none;
  box-shadow: none;
}

.todo-status-count:not(.active) {
  opacity: 0.5;
}

.todo-count-pending {
  display: inline-flex;
  align-items: center;
  color: #222;
}

.todo-count-completed {
  display: inline-flex;
  align-items: center;
  color: #222;
}

.todo-count-cancelled {
  display: inline-flex;
  align-items: center;
  color: #222;
}

.todo-count-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  border-radius: 50%;
  vertical-align: middle;
}

.todo-count-pending .todo-count-dot {
  background-color: #f59e0b;
}

.todo-count-completed .todo-count-dot {
  background-color: #16a34a;
}

.todo-count-cancelled .todo-count-dot {
  background-color: #ef4444;
}

.todo-actions {
  position: relative;
  margin-left: auto;
  gap: 5px;
}

.todo-actions > button {
  height: 27px;
  box-sizing: border-box;
}

.todo-filter-summary > .todo-filter-action,
.todo-filter-summary > .todo-filter-action .button-filter-main,
.todo-filter-summary > .todo-filter-action .button-filter-main > button {
  height: 27px;
  box-sizing: border-box;
}

.todo-actions button,
.todo-filter-panel button {
  min-height: 27px;
  padding: 3px 9px;
}

.todo-filter-action {
  position: relative;
}

.todo-filter-panel {
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 100%;
  padding: 4px;
  box-sizing: border-box;
}

.todo-filter-date-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.todo-filter-actions {
  display: flex;
  justify-content: space-between;
  gap: 5px;
  margin-top: 5px;
}

.todo-filter-actions-right {
  display: flex;
  gap: 5px;
}

.todo-filter-show-cancelled {
  align-self: flex-start;
}

.todo-context-line,
.todo-help-text {
  color: #666;
  padding: 3px 0;
}

.todo-existing-remarks {
  max-height: 80px;
  overflow-y: auto;
  padding: 5px 7px;
  white-space: pre-wrap;
  color: #555;
  background-color: #f5f5f5;
  border: 1px solid #d8dee8;
}

.todo-editor {
  display: flex;
  flex-direction: column;
  gap: 5px;
  text-align: left;
}

.todo-editor > .todo-editor-remarks {
  flex: 0 0 120px;
  min-height: 120px;
  height: 120px;
}

.todo-editor-reactivate {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

@media (max-width: 900px) {
  .todo-work-area {
    grid-template-columns: 1fr;
  }

  .todo-editor-container {
    padding-top: 10px;
    padding-left: 0;
    border-top: 1px solid #d8dee8;
    border-left: 0;
  }

}
</style>
